import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useCookieConsent, CookiePreferences } from '../../context/CookieConsentContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { ROUTES } from '../routes/paths';

// Styled components for the consent banner
const ConsentBanner = styled.div<{ isDarkMode: boolean }>`
  position: fixed;
  bottom: 2rem;
  left: 2rem;
  max-width: 400px;
  background-color: ${props => props.isDarkMode ? '#2a2c2e' : '#fff'};
  color: ${props => props.isDarkMode ? '#f3f4f6' : 'inherit'};
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.15);
  border-radius: 12px;
  padding: 1.5rem;
  z-index: 9999;
  animation: slideIn 0.5s ease-out forwards;
  border-left: 4px solid #1977cc;
  
  @keyframes slideIn {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: 576px) {
    left: 1rem;
    right: 1rem;
    bottom: 1rem;
    max-width: calc(100% - 2rem);
  }
`;

const ConsentTitle = styled.h5<{ isDarkMode: boolean }>`
  color: ${props => props.isDarkMode ? '#f3f4f6' : '#2c4964'};
  margin-bottom: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  
  i {
    margin-right: 0.5rem;
    color: #1977cc;
  }
`;

const ConsentText = styled.p<{ isDarkMode: boolean }>`
  color: ${props => props.isDarkMode ? '#d1d5db' : '#555'};
  font-size: 0.9rem;
  margin-bottom: 1.25rem;
  line-height: 1.5;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const AcceptButton = styled.button`
  background-color: #1977cc;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #166ab9;
    transform: translateY(-2px);
  }
`;

const RejectButton = styled.button<{ isDarkMode: boolean }>`
  background-color: transparent;
  color: ${props => props.isDarkMode ? '#d1d5db' : '#555'};
  border: 1px solid ${props => props.isDarkMode ? '#4b5563' : '#ddd'};
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.isDarkMode ? '#374151' : '#f8f9fa'};
    color: ${props => props.isDarkMode ? '#f3f4f6' : '#333'};
  }
`;

const CustomizeButton = styled(RejectButton)`
  &:hover {
    border-color: #1977cc;
    color: #1977cc;
  }
`;

const BackButton = styled(RejectButton)`
  display: flex;
  align-items: center;
  
  i {
    margin-right: 0.25rem;
  }
  
  &:hover {
    border-color: #1977cc;
    color: #1977cc;
  }
`;

const DismissButton = styled.button<{ isDarkMode: boolean }>`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: transparent;
  border: none;
  color: ${props => props.isDarkMode ? '#9ca3af' : '#aaa'};
  font-size: 1.1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  
  &:hover {
    color: ${props => props.isDarkMode ? '#f3f4f6' : '#555'};
  }
`;

const CookieOption = styled.div<{ isDarkMode: boolean }>`
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid ${props => props.isDarkMode ? '#4b5563' : '#eee'};
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 1rem;
    padding-bottom: 0;
  }
`;

const CookieOptionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const CookieOptionTitle = styled.h5<{ isDarkMode: boolean }>`
  color: ${props => props.isDarkMode ? '#f3f4f6' : '#2c4964'};
  margin: 0;
  font-weight: 600;
`;

const CookieOptionDescription = styled.p<{ isDarkMode: boolean }>`
  color: ${props => props.isDarkMode ? '#d1d5db' : '#555'};
  font-size: 0.9rem;
  margin-bottom: 0;
`;

const Switch = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
`;

const SwitchInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  
  &:checked + span {
    background-color: #1977cc;
  }
  
  &:checked + span:before {
    transform: translateX(26px);
  }
  
  &:disabled + span {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Slider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 24px;
  
  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.4s;
    border-radius: 50%;
  }
`;

const PolicyLink = styled(Link)<{ isDarkMode: boolean }>`
  color: #1977cc;
  text-decoration: underline;
  font-size: 0.9rem;
  display: block;
  margin-top: 1rem;
  margin-bottom: 1rem;
  
  &:hover {
    color: #166ab9;
  }
`;

const CookieConsentBanner: React.FC = () => {
  const { 
    acceptAllCookies, 
    rejectCookies, 
    dismissConsent, 
    customizeCookies,
    showConsentBanner,
    cookiePreferences
  } = useCookieConsent();
  const { language } = useLanguage();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  
  const [localPreferences, setLocalPreferences] = useState<CookiePreferences>({
    ...cookiePreferences
  });
  
  const [showCustomizePanel, setShowCustomizePanel] = useState(false);
  
  const handleToggle = (cookieType: keyof CookiePreferences) => {
    if (cookieType === 'necessary') return; // Can't toggle necessary cookies
    
    setLocalPreferences(prev => ({
      ...prev,
      [cookieType]: !prev[cookieType]
    }));
  };
  
  const handleSave = () => {
    customizeCookies(localPreferences);
  };
  
  const openCustomize = () => {
    setLocalPreferences({...cookiePreferences});
    setShowCustomizePanel(true);
  };
  
  const closeCustomize = () => {
    setShowCustomizePanel(false);
  };
  
  if (!showConsentBanner) {
    return null;
  }
  
  return (
    <ConsentBanner isDarkMode={isDarkMode}>
      <DismissButton onClick={dismissConsent} aria-label="Dismiss" isDarkMode={isDarkMode}>
        <i className="bi bi-x"></i>
      </DismissButton>
      
      {!showCustomizePanel ? (
        <>
          <ConsentTitle isDarkMode={isDarkMode}>
            <i className="bi bi-shield-check"></i>
            {language === 'en' ? 'Cookie Preferences' : 'Preferências de Cookies'}
          </ConsentTitle>
          
          <ConsentText isDarkMode={isDarkMode}>
            {language === 'en' 
              ? 'We use cookies to enhance your browsing experience, save your language preferences, and remember your theme choice. You can customize your preferences or learn more about our cookies.' 
              : 'Utilizamos cookies para melhorar sua experiência de navegação, salvar suas preferências de idioma e lembrar sua escolha de tema. Você pode personalizar suas preferências ou saber mais sobre nossos cookies.'}
          </ConsentText>
          
          <PolicyLink to={ROUTES.COOKIES} isDarkMode={isDarkMode}>
            {language === 'en' ? 'Learn more about our cookies' : 'Saiba mais sobre nossos cookies'}
          </PolicyLink>
          
          <ButtonGroup>
            <RejectButton onClick={rejectCookies} isDarkMode={isDarkMode}>
              {language === 'en' ? 'Reject All' : 'Rejeitar Todos'}
            </RejectButton>
            <CustomizeButton onClick={openCustomize} isDarkMode={isDarkMode}>
              {language === 'en' ? 'Customize' : 'Personalizar'}
            </CustomizeButton>
            <AcceptButton onClick={acceptAllCookies}>
              {language === 'en' ? 'Accept All' : 'Aceitar Todos'}
            </AcceptButton>
          </ButtonGroup>
        </>
      ) : (
        <>
          <ConsentTitle isDarkMode={isDarkMode}>
            <i className="bi bi-sliders"></i>
            {language === 'en' ? 'Cookie Preferences' : 'Preferências de Cookies'}
          </ConsentTitle>
          
          <CookieOption isDarkMode={isDarkMode}>
            <CookieOptionHeader>
              <CookieOptionTitle isDarkMode={isDarkMode}>
                {language === 'en' ? 'Necessary Cookies' : 'Cookies Necessários'}
              </CookieOptionTitle>
              <Switch>
                <SwitchInput type="checkbox" checked={true} disabled />
                <Slider />
              </Switch>
            </CookieOptionHeader>
            <CookieOptionDescription isDarkMode={isDarkMode}>
              {language === 'en' 
                ? 'These cookies are required for the website to function properly and cannot be disabled.'
                : 'Estes cookies são necessários para o funcionamento adequado do site e não podem ser desativados.'}
            </CookieOptionDescription>
          </CookieOption>
          
          <CookieOption isDarkMode={isDarkMode}>
            <CookieOptionHeader>
              <CookieOptionTitle isDarkMode={isDarkMode}>
                {language === 'en' ? 'Language Preference' : 'Preferência de Idioma'}
              </CookieOptionTitle>
              <Switch>
                <SwitchInput 
                  type="checkbox" 
                  checked={localPreferences.language} 
                  onChange={() => handleToggle('language')}
                />
                <Slider />
              </Switch>
            </CookieOptionHeader>
            <CookieOptionDescription isDarkMode={isDarkMode}>
              {language === 'en' 
                ? 'These cookies remember your language preference for your next visit.'
                : 'Estes cookies lembram sua preferência de idioma para sua próxima visita.'}
            </CookieOptionDescription>
          </CookieOption>
          
          <CookieOption isDarkMode={isDarkMode}>
            <CookieOptionHeader>
              <CookieOptionTitle isDarkMode={isDarkMode}>
                {language === 'en' ? 'Theme Preference' : 'Preferência de Tema'}
              </CookieOptionTitle>
              <Switch>
                <SwitchInput 
                  type="checkbox" 
                  checked={localPreferences.theme} 
                  onChange={() => handleToggle('theme')}
                />
                <Slider />
              </Switch>
            </CookieOptionHeader>
            <CookieOptionDescription isDarkMode={isDarkMode}>
              {language === 'en' 
                ? 'These cookies remember your light/dark theme preference for your next visit.'
                : 'Estes cookies lembram sua preferência de tema claro/escuro para sua próxima visita.'}
            </CookieOptionDescription>
          </CookieOption>
          
          <ButtonGroup>
            <BackButton onClick={closeCustomize} isDarkMode={isDarkMode}>
              <i className="bi bi-arrow-left"></i>
              {language === 'en' ? 'Back' : 'Voltar'}
            </BackButton>
            <AcceptButton onClick={handleSave}>
              {language === 'en' ? 'Save Preferences' : 'Salvar Preferências'}
            </AcceptButton>
          </ButtonGroup>
        </>
      )}
    </ConsentBanner>
  );
};

export default CookieConsentBanner; 